import { fetchAdminSenderProxyStats } from '@/api/admin';
import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import type { SenderProxyConfig } from '@/entities/sender.entity';
import SenderProxyStatsCards from '@/pages/admin/settings/sender-proxy-stats-cards';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

interface SenderProxyMiniSummaryProps {
	value: SenderProxyConfig;
	onChange: (next: SenderProxyConfig) => void;
	onProxyCountChange?: (count: number) => void;
	disabled?: boolean;
}

export default function SenderProxyMiniSummary({
	value,
	onChange,
	onProxyCountChange,
	disabled = false,
}: SenderProxyMiniSummaryProps) {
	const navigate = useNavigate();
	const [stats, setStats] = useState<Awaited<ReturnType<typeof fetchAdminSenderProxyStats>> | null>(null);
	const onCountRef = useRef(onProxyCountChange);
	const proxyCountRef = useRef(value.proxyCount);

	useEffect(() => {
		onCountRef.current = onProxyCountChange;
	}, [onProxyCountChange]);

	useEffect(() => {
		proxyCountRef.current = value.proxyCount;
	}, [value.proxyCount]);

	useEffect(() => {
		let cancelled = false;
		fetchAdminSenderProxyStats()
			.then(data => {
				if (!cancelled) {
					setStats(data);
					const total = data.proxy.total;
					if (total !== proxyCountRef.current) {
						onCountRef.current?.(total);
					}
				}
			})
			.catch(() => {
				if (!cancelled) setStats(null);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	function patchEnabled(enabled: boolean) {
		const total = stats?.proxy.total ?? value.proxyCount;
		if (enabled && total === 0) {
			toast.error('Загрузите прокси перед включением пула');
			return;
		}
		onChange({ ...value, enabled, proxyCount: total });
	}

	return (
		<div className='flex flex-col gap-4'>
			<div className='flex items-center justify-between gap-3'>
				<div className='flex flex-col gap-0.5'>
					<Label htmlFor='sender-proxy-enabled'>Использовать прокси</Label>
					<span className='text-xs text-muted-foreground'>
						В пуле: {stats?.proxy.total ?? value.proxyCount} · available: {stats?.proxy.available ?? '—'}
					</span>
				</div>
				<Switch
					id='sender-proxy-enabled'
					checked={value.enabled}
					onCheckedChange={patchEnabled}
					disabled={disabled}
				/>
			</div>

			<SenderProxyStatsCards stats={stats} />

			<Button
				type='button'
				variant='outline'
				className='self-start'
				onClick={() => navigate(ROUTES.adminSenderProxies, { state: { from: ROUTES.adminSettings } })}
			>
				Управление прокси →
			</Button>
		</div>
	);
}
